/**
 * A condição que ficou pela metade no editor e some ao salvar.
 *
 * ## O que acontecia
 *
 * No editor de filtro, montar uma condição tem dois passos: preencher campo,
 * operador e valor, e **clicar em "Adicionar condição"**. Só o segundo passo
 * põe a condição na lista do filtro.
 *
 * Quem preenchia e clicava direto em "Salvar" salvava um filtro **sem
 * condição nenhuma** -- sem erro, sem aviso, com a mensagem de sucesso de
 * sempre. E filtro sem condição não filtra nada: ele passa a mostrar a
 * listagem inteira.
 *
 * É assim que "filtrei por mim como responsável" devolve chamado dos outros.
 * O relato (Daniel, 28/10/2026) descreve exatamente esse resultado, e esse
 * é o modo de falha mais caro que existe nesta tela: o filtro parece
 * aplicado, a tela parece certa, e o que ela mostra é tudo.
 *
 * O descarte em si está no `conditions.filter(...)` do payload, e ele é
 * necessário -- condição sem campo não pode ir para o servidor. O que não
 * pode é descartar **em silêncio** o que a pessoa acabou de preencher.
 *
 * ## Por que num arquivo, e não dentro do componente
 *
 * Há DOIS caminhos de salvamento no modal (`handleSave` e
 * `handleSaveAndApply`), e a regra precisa valer nos dois. Escrita duas
 * vezes, uma das cópias ficaria para trás -- e o caminho esquecido voltaria
 * a gravar o filtro vazio sem avisar.
 */

/**
 * O editor tem uma condição começada que ainda não entrou na lista?
 *
 * @param {object} params
 * @param {boolean} params.editorAberto   o editor de condição está aberto
 * @param {'standard'|'custom'|null} params.tipo
 * @param {object} params.condicao        a condição em edição (`tempCondition`)
 * @returns {boolean}
 */
export const temCondicaoPendente = ({ editorAberto, tipo, condicao } = {}) => {
    if (!editorAberto) return false;

    /*
     * Abrir o editor e não escolher nada não é condição pendente: é alguém
     * que abriu e desistiu. Avisar aí seria barrar o salvamento por causa de
     * um formulário em branco.
     */
    const escolheuCampo = tipo === 'custom'
        ? condicao?.field_id != null && condicao.field_id !== ''
        : typeof condicao?.field_name === 'string' && condicao.field_name !== '';

    return escolheuCampo;
};

/**
 * O aviso a mostrar, ou string vazia quando não há nada pendente.
 *
 * O texto diz o que fazer E o que acontece se não for feito: sem a
 * consequência, "adicione a condição" parece burocracia, e a pessoa fecha o
 * aviso e salva assim mesmo -- de novo com o filtro mostrando tudo.
 */
export const avisoDeCondicaoNaoAdicionada = (params) => {
    if (!temCondicaoPendente(params)) return '';

    return 'Você preencheu uma condição e não clicou em "Adicionar condição". '
        + 'Adicione-a ou cancele antes de salvar: do jeito que está, ela se perde '
        + 'e o filtro passa a mostrar todos os chamados.';
};
